import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { posApi, type StorePaymentGatewayRequest } from '@/api/pos.api';
import { storesApi } from '@/api/stores.api';
import { PosHardwareSetupPanel } from '@/components/pos/hardware/PosHardwareSetupPanel';
import { useAppSelector } from '@/hooks/useAppSelector';
import {
  loadStoreModifiers,
  saveStoreModifiers,
  setFbModifiersEnabled,
  isFbModifiersEnabled,
  type PosModifierOption,
} from '@/utils/posModifiers';

import { PageHeader } from '@/components/layout/PageHeader';
import { notify } from '@/core/toast/notify';
import { KeyValueTable } from '@/components/ui/KeyValueTable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { GatewayConfigBuilder } from '@/components/ui/ActionConfigBuilder';
const PROVIDERS = ['RAZORPAY', 'STRIPE', 'STRIPE_TERMINAL', 'PAYTM', 'CASHFREE', 'PHONEPE', 'PAYU'];
const card = 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900';

type Tab = 'gateways' | 'counters' | 'hardware' | 'taxes' | 'terminals' | 'modifiers';

export function PosSetupPage() {
  const store = useAppSelector((s) => s.app.selectedStore);
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('gateways');
  const [provider, setProvider] = useState('RAZORPAY');
  const [displayName, setDisplayName] = useState('');
  const [gatewayTestMode, setGatewayTestMode] = useState(true);
  const [gatewayConfig, setGatewayConfig] = useState<Record<string, string>>({
    keyId: '',
    keySecret: '',
  });
  const [counterName, setCounterName] = useState('');
  const [counterCode, setCounterCode] = useState('');
  const [fbEnabled, setFbEnabled] = useState(false);
  const [modifiers, setModifiers] = useState<PosModifierOption[]>([]);

  const { data: terminals } = useQuery({
    queryKey: ['pos-terminals', store?.id],
    queryFn: () => posApi.listTerminals(store!.id),
    enabled: !!store && tab === 'terminals',
  });

  useEffect(() => {
    if (!store) return;
    setFbEnabled(isFbModifiersEnabled(store.id));
    setModifiers(loadStoreModifiers(store.id));
  }, [store?.id]);

  useEffect(() => {
    setGatewayConfig({});
  }, [provider]);

  const { data: gateways = [] } = useQuery({
    queryKey: ['pos-gateways', store?.id],
    queryFn: () => posApi.listGateways(store!.id),
    enabled: !!store,
  });

  const { data: counters } = useQuery({
    queryKey: ['pos-counters', store?.id],
    queryFn: () => posApi.listCounters(store!.id),
    enabled: !!store,
  });

  const { data: hardware, refetch: refetchHardware } = useQuery({
    queryKey: ['pos-hardware', store?.id],
    queryFn: () => posApi.getHardwareConfig(store!.id),
    enabled: !!store && tab === 'hardware',
  });

  const { data: taxConfig } = useQuery({
    queryKey: ['store-tax', store?.id],
    queryFn: () => storesApi.getTaxConfig(store!.id),
    enabled: !!store && tab === 'taxes',
  });

  const saveGateway = useMutation({
    mutationFn: () =>
      posApi.saveGateway({
        storeId: store!.id,
        providerCode: provider,
        displayName: displayName || provider,
        configJson: JSON.stringify(gatewayConfig),
        enabled: true,
        testMode: gatewayTestMode,
        sortOrder: gateways.length,
      } satisfies StorePaymentGatewayRequest),
    onSuccess: () => {
      notify.success('Gateway saved');
      qc.invalidateQueries({ queryKey: ['pos-gateways'] });
    },
    onError: () => notify.error('Failed to save gateway'),
  });

  const createCounter = useMutation({
    mutationFn: () =>
      posApi.createCounter({
        storeId: store!.id,
        name: counterName.trim(),
        code: counterCode.trim(),
      }),
    onSuccess: () => {
      notify.success('Counter created');
      setCounterName('');
      setCounterCode('');
      qc.invalidateQueries({ queryKey: ['pos-counters'] });
    },
    onError: () => notify.error('Failed to create counter'),
  });

  if (!store) {
    return <div className="flex h-full items-center justify-center text-slate-500">Select a store first.</div>;
  }

  return (
    <div className="h-full overflow-auto p-6">
      <PageHeader
        title="POS Setup"
        description={`${store.name} — counters, gateways, hardware, taxes`}
      />

      <div className="mt-4 flex gap-2">
        {(['gateways', 'counters', 'terminals', 'hardware', 'modifiers', 'taxes'] as Tab[]).map((t) => (
          <Button
            key={t}
            type="button"
            size="sm"
            variant={tab === t ? 'primary' : 'secondary'}
            className="capitalize"
            onClick={() => setTab(t)}
          >
            {t}
          </Button>
        ))}
      </div>

      {tab === 'gateways' && (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className={card}>
            <h2 className="mb-4 font-semibold">Add payment gateway</h2>
            <div className="space-y-3">
              <Select
                value={provider}
                onChange={(v) => setProvider(v)}
                options={PROVIDERS.map((p) => ({ value: p, label: p }))}
              />
              <Input placeholder="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              <GatewayConfigBuilder
                provider={provider}
                value={gatewayConfig}
                onChange={setGatewayConfig}
              />
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  className="rounded accent-primary-600"
                  checked={gatewayTestMode}
                  onChange={(e) => setGatewayTestMode(e.target.checked)}
                />
                Test mode (simulated terminal / sandbox keys)
              </label>
              <Button
                type="button"
                onClick={() => saveGateway.mutate()}
                disabled={saveGateway.isPending}
                loading={saveGateway.isPending}
              >
                Save gateway
              </Button>
            </div>
          </div>
          <div className={card}>
            <h2 className="mb-4 font-semibold">Configured gateways</h2>
            <ul className="space-y-2">
              {gateways.map((g) => (
                <li key={g.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800">
                  <span>
                    {g.displayName} <span className="text-xs text-slate-500">({g.providerCode})</span>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 dark:text-red-400"
                    onClick={() => posApi.deleteGateway(g.id).then(() => qc.invalidateQueries({ queryKey: ['pos-gateways'] }))}
                  >
                    Remove
                  </Button>
                </li>
              ))}
              {gateways.length === 0 && <li className="text-sm text-slate-500">No gateways configured.</li>}
            </ul>
          </div>
        </div>
      )}

      {tab === 'counters' && (
        <div className={`mt-6 ${card}`}>
          <h2 className="mb-4 font-semibold">Billing counters</h2>
          <ul className="mb-4 space-y-1">
            {counters?.content.map((c) => (
              <li key={c.id} className="text-sm">
                {c.name} ({c.code}) — ID {c.id}
              </li>
            ))}
          </ul>
          <p className="mb-2 text-xs text-slate-500">Create billing counters for this store.</p>
          <div className="flex gap-2">
            <Input placeholder="Name" value={counterName} onChange={(e) => setCounterName(e.target.value)} />
            <Input placeholder="Code" value={counterCode} onChange={(e) => setCounterCode(e.target.value)} />
            <Button
              type="button"
              disabled={!counterName.trim() || !counterCode.trim() || createCounter.isPending}
              loading={createCounter.isPending}
              onClick={() => createCounter.mutate()}
            >
              Add
            </Button>
          </div>
        </div>
      )}

      {tab === 'hardware' && hardware && (
        <div className="mt-6">
          <p className="mb-4 text-sm text-slate-500">
            Connect devices per counter/store. Barcode scanners are plug-and-play (USB keyboard mode).
          </p>
          <PosHardwareSetupPanel
            storeId={store.id}
            hardware={hardware}
            onSaved={() => void refetchHardware()}
          />
        </div>
      )}

      {tab === 'taxes' && (
        <div className={`mt-6 ${card}`}>
          <h2 className="mb-4 font-semibold">Store tax configuration</h2>
          <KeyValueTable data={(taxConfig ?? {}) as Record<string, unknown>} embedded emptyTitle="No tax configuration" />
          <p className="mt-2 text-xs text-slate-500">Edit via Org settings → Store tax config API.</p>
        </div>
      )}

      {tab === 'terminals' && (
        <div className={`mt-6 ${card}`}>
          <h2 className="mb-4 font-semibold">Payment terminals</h2>
          <p className="mb-3 text-sm text-slate-500">
            Terminals auto-register when POS billing opens with a counter selected. Heartbeat keeps status ONLINE.
          </p>
          <ul className="space-y-2 text-sm">
            {terminals?.content.map((t) => (
              <li key={t.id} className="flex justify-between rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-800">
                <span>
                  {t.deviceName ?? t.deviceId} — counter {t.counterId ?? '—'}
                </span>
                <span className="text-xs text-slate-500">ID {t.id}</span>
              </li>
            ))}
            {(terminals?.content.length ?? 0) === 0 && (
              <li className="text-slate-500">No terminals registered yet — open POS billing to register this device.</li>
            )}
          </ul>
        </div>
      )}

      {tab === 'modifiers' && store && (
        <div className={`mt-6 ${card}`}>
          <h2 className="mb-4 font-semibold">F&amp;B modifiers</h2>
          <label className="mb-4 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={fbEnabled}
              onChange={(e) => {
                setFbEnabled(e.target.checked);
                setFbModifiersEnabled(store.id, e.target.checked);
              }}
            />
            Prompt for modifiers when adding products in POS
          </label>
          <ul className="mb-4 space-y-2">
            {modifiers.map((m) => (
              <li key={m.id} className="flex gap-2 text-sm">
                <Input
                  value={m.name}
                  onChange={(e) =>
                    setModifiers((prev) => prev.map((x) => (x.id === m.id ? { ...x, name: e.target.value } : x)))
                  }
                />
                <Input
                  className="w-24"
                  type="number"
                  value={m.price}
                  onChange={(e) =>
                    setModifiers((prev) =>
                      prev.map((x) => (x.id === m.id ? { ...x, price: Number(e.target.value) } : x)),
                    )
                  }
                />
              </li>
            ))}
          </ul>
          <Button
            type="button"
            onClick={() => {
              saveStoreModifiers(store.id, modifiers);
              notify.success('Modifiers saved');
            }}
          >
            Save modifiers
          </Button>
        </div>
      )}
    </div>
  );
}
